import type { ToolDefinition } from '../../types';
import { Tool } from '../Tool';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { ApiService, TaskResultMessageTypeEnum } from 'js/src/client';
import WebSocketClient from 'js/src/libs/websocket-client';
import { isErrorObject } from 'js/src/types';

interface SketchWebImageSearchArgs {
    query: string;
    num_results?: number;
    thoughts?: string;
}

interface SketchWebImageSearchResponse {
    query: string;
    num_results_requested: number;
    num_results_found: number;
    num_assets_created: number;
    asset_uuids: string[];
    search_results: Array<{
        title: string;
        link: string;
    }>;
}

export class SketchWebImageSearchTool extends Tool<SketchWebImageSearchArgs> {
    public static readonly toolName = 'web_image_search';

    public override canRunAsync = true;
    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: SketchWebImageSearchTool.toolName,
                description:
                    "Fetches images from web search engines. ONLY call this tool when: (1) user explicitly mentions 'web', 'internet', or 'online' in their image request, OR (2) user asks for specific current products, people, or recent events (e.g., 'iPhone 16 Pro', 'Elon Musk', 'CES 2025'). Never use it for broad or generic image searches — assume those can be handled with built-in stock media.",
                parameters: {
                    type: 'object',
                    properties: {
                        query: {
                            type: 'string',
                            description:
                                'The image search query string to find relevant images for the video content',
                        },
                        num_results: {
                            type: 'number',
                            description:
                                'Number of image results to return (optional, defaults to system setting)',
                        },
                        thoughts: {
                            type: 'string',
                            description:
                                'Reasoning for why this image search is being performed (optional)',
                        },
                    },
                    required: ['query'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Searching web images';
    }

    public get statusIcon(): string {
        return 'image_search';
    }

    public async execute(args: SketchWebImageSearchArgs) {
        const { query, num_results, thoughts } = args;

        try {
            const response = await ApiService.sketchWebImageSearchCreate({
                body: {
                    query: query,
                    num_results: num_results,
                    thoughts: thoughts,
                },
            });

            const ws = WebSocketClient.getInstance();
            const result = await ws.waitForMessage<SketchWebImageSearchResponse>(
                response.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'sketch_web_image_search',
            );

            if (result.num_assets_created === 0) {
                return `Web image search for "${query}" completed but no image assets were created. Found ${result.num_results_found} results but none could be converted to usable assets.`;
            }

            // Add assets to MediaRegistry
            const mediaRegistry = MediaRegistry.getInstance();
            const representations = [];

            if (result.asset_uuids && result.asset_uuids.length > 0) {
                // Fetch asset data from API using UUIDs
                const uuidsString = result.asset_uuids.join(',');
                try {
                    const assetResponse = await ApiService.bulkMediaAssetsList({
                        query: { uuids: uuidsString },
                    });

                    // Add each asset to registry and collect representations
                    if (assetResponse.data) {
                        for (const asset of assetResponse.data) {
                            if (asset.background) {
                                const id = mediaRegistry.addItem(asset);
                                const representation = mediaRegistry.getRepresentation(id);
                                if (representation) {
                                    representations.push(representation);
                                }
                            }
                        }
                    }
                } catch (apiError) {
                    console.warn('Failed to fetch asset details for MediaRegistry:', apiError);
                }
            }

            let formattedResponse = `Web image search completed for query: "${query}"\n\n`;
            formattedResponse += '**Results Summary:**\n';
            formattedResponse += `- Assets Created: ${result.num_assets_created}\n\n`;

            if (representations.length > 0) {
                formattedResponse += '**Created Assets:**\n';
                formattedResponse += JSON.stringify(representations);
                formattedResponse += '\n\n';
            }

            return formattedResponse;
        } catch (error) {
            const errorMessage = isErrorObject(error)
                ? error.error.message
                : error instanceof Error
                  ? error.message
                  : 'Unknown error occurred';
            return `Error searching web images: ${errorMessage}`;
        }
    }
}
